<template>
	<div class="container-fluid mt-4">
		<div class="d-flex justify-content-between align-items-center mb-4">
			<h2>
				<i class="bi bi-grid-3x3-gap-fill me-2"></i>
				Vision Tasks
			</h2>
		</div>

		<div v-if="loading" class="row mb-4">
			<div class="col-md-4">
				<div class="card">
					<div class="card-body">
						<div class="spinner-border text-primary" role="status">
							<span class="visually-hidden">Loading...</span>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div v-else-if="error" class="alert alert-danger">{{ error }}</div>

		<div v-else-if="stats" class="row mb-4">
			<div class="col-md-3">
				<div class="card text-bg-light">
					<div class="card-body">
						<div class="fs-3 fw-bold">{{ stats.totalPools }}</div>
						<div class="text-muted">Total pools</div>
					</div>
				</div>
			</div>
			<div class="col-md-3">
				<div class="card text-bg-light">
					<div class="card-body">
						<div class="fs-3 fw-bold">{{ stats.reviewedPools }}</div>
						<div class="text-muted">Reviewed pools</div>
					</div>
				</div>
			</div>
			<div class="col-md-3">
				<div class="card text-bg-light">
					<div class="card-body">
						<div class="fs-3 fw-bold">{{ unreviewedPools }}</div>
						<div class="text-muted">Unreviewed pools</div>
					</div>
				</div>
			</div>
		</div>

		<div v-if="stats && stats.byReviewer.length > 0" class="card mb-4">
			<div class="card-header">
				<i class="bi bi-trophy me-2"></i>
				Review leaderboard
			</div>
			<div class="table-responsive">
				<table class="table table-hover align-middle mb-0">
					<thead class="table-light">
						<tr>
							<th style="width: 50px"></th>
							<th>Reviewer</th>
							<th class="text-end" style="width: 120px">Reviewed</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="(rc, idx) in stats.byReviewer" :key="rc.reviewerId">
							<td class="text-muted">{{ idx + 1 }}</td>
							<td class="fw-semibold">{{ rc.displayName }}</td>
							<td class="text-end">{{ rc.count }}</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>

		<div class="row">
			<div class="col-md-4">
				<RouterLink class="card-link" to="/_/sudo/vision-tasks/pool-corpus">
					<div class="card">
						<img
							alt="pool corpus"
							class="card-img-top"
							src="/static/img/vision-task.png"
						/>
						<div class="card-body">
							<h1>Pool Corpus Review</h1>
							<p>
								Review machine-learning detections from satellite imagery
								analysis tile-by-tile.
							</p>
						</div>
					</div>
				</RouterLink>
			</div>
			<div class="col-md-4">
				<RouterLink
					class="card-link"
					to="/_/sudo/vision-tasks/position-inference-anchoring"
				>
					<div class="card-body">
						<img
							alt="position inference anchoring"
							class="card-img-top"
							src="/static/img/pool-overhead.jpg"
						/>
						<div class="card-body">
							<h1>Position Inference Anchoring</h1>
							<p>
								Anchor pool detections against Planet, Blue Excess, NDWI, and
								NIR imagery variants.
							</p>
						</div>
					</div>
				</RouterLink>
			</div>
			<div class="col-md-4">
				<RouterLink
					class="card-link"
					to="/_/sudo/vision-tasks/tile-confirmation"
				>
					<div class="card">
						<img
							alt="tile confirmation"
							class="card-img-top"
							src="/static/img/pool-overhead.jpg"
						/>
						<div class="card-body">
							<h1>Tile Confirmation</h1>
							<p>
								Confirm review tiles: pool-free (E), partial (P), or all pools
								found (A) — with a found-pools overlay.
							</p>
						</div>
					</div>
				</RouterLink>
			</div>
			<div class="col-md-4">
				<RouterLink class="card-link" to="/_/sudo/vision-tasks/correct-finding">
					<div class="card">
						<img
							alt="correct finding"
							class="card-img-top"
							src="/static/img/pool-overhead.jpg"
						/>
						<div class="card-body">
							<h1>Correct Finding</h1>
							<p>
								Resolve reviewer-flagged false negatives and false positives:
								draw, redraw, reposition, or delete the affected pool.
							</p>
						</div>
					</div>
				</RouterLink>
			</div>
			<div class="col-md-4">
				<RouterLink class="card-link" to="/_/sudo/vision-tasks/pool-condition">
					<div class="card">
						<img
							alt="pool condition"
							class="card-img-top"
							src="/static/img/pool-overhead.jpg"
						/>
						<div class="card-body">
							<h1>Pool Condition Review</h1>
							<p>
								Record each pool's water condition from its satellite imagery:
								maintained, unmaintained, partial, or dry.
							</p>
						</div>
					</div>
				</RouterLink>
			</div>
		</div>
	</div>
</template>

<style scoped>
a.card-link {
	text-decoration: none;
}
.card-link .card-img-top {
	height: 200px;
	object-fit: cover;
	object-position: center;
}
.card-link .card {
	height: 100%;
}
.card-link {
	text-decoration: none;
	color: inherit;
	display: block;
	height: 100%;
}
</style>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { apiClient } from "@/client";
import type { VisionReviewStats } from "@/type/api";

const loading = ref(false);
const error = ref("");
const stats = ref<VisionReviewStats | null>(null);

const unreviewedPools = computed(() =>
	stats.value ? stats.value.totalPools - stats.value.reviewedPools : 0,
);

async function loadStats() {
	loading.value = true;
	error.value = "";
	try {
		stats.value = await apiClient.JSONGet<VisionReviewStats>(
			"/api/vision/review/stats",
		);
	} catch (e: any) {
		error.value = e?.message ?? "Failed to load review statistics.";
	} finally {
		loading.value = false;
	}
}

onMounted(loadStats);
</script>
